import { createContext, useCallback, useContext, useState, useEffect, useMemo, useRef, ReactNode } from 'react'
import { useDistributionCentersQuery, WarehouseExecutionSystem } from '../../../graphql'
import { useLocalStorage } from '../../../hooks/useLocalStorage'

type DcOption = {
	id: string
	name: string
	/**
	 * The system that picks this DC's orders. MANUAL means its picking is done by
	 * hand, which is the whole premise of the pick-order screens — inventory-api
	 * rejects a pick order at any other DC.
	 */
	warehouseExecutionSystem: WarehouseExecutionSystem | null
}

type DcAdminContextValue = {
	selectedDcId: string | null
	setSelectedDcId: ( id: string | null ) => void
	dcs: DcOption[]
	loading: boolean
}

/**
 * Where the header's choice of DC is kept between visits.
 *
 * Per browser rather than per account: this is a convenience, not a
 * preference worth a column, and someone who signs in elsewhere simply gets
 * the default again. Namespaced the way `STORAGE_KEYS` already names its
 * entries, so the key says which screen owns it.
 */
const SELECTED_DC_STORAGE_KEY = 'dc-admin/selectedDc'

const DcAdminContext = createContext<DcAdminContextValue | null>( null )

export function DcAdminProvider( { children }: { children: ReactNode } ) {
	// A DC admin works the same site every day, so re-choosing it after every
	// reload was the first thing they did on landing.
	const [ rememberedDcId, rememberDcId ] = useLocalStorage<string | null>( SELECTED_DC_STORAGE_KEY, null )
	const [ selectedDcId, setSelectedDcIdState ] = useState<string | null>( null )
	const initializedRef = useRef( false )

	const { data, loading } = useDistributionCentersQuery( {
		variables: { paging: { first: 100 } },
	} )

	const dcs: DcOption[] = useMemo(
		() => data?.inventoryLocations.edges.map(
			( edge ) => ( {
				id: edge.node.id,
				name: edge.node.name,
				warehouseExecutionSystem: edge.node.warehouse?.warehouseExecutionSystem ?? null,
			} ),
		) ?? [],
		[ data ],
	)

	useEffect( () => {
		if ( loading || dcs.length === 0 || initializedRef.current ) {
			return
		}
		initializedRef.current = true
		// A remembered DC counts only while it is still one this user can pick:
		// access changes and locations get archived, and selecting one that is gone
		// would scope every query on these screens to an id the list does not hold.
		// The memory is left as it is rather than overwritten, so a DC that comes
		// back is still the one waiting.
		const remembered = dcs.find( ( dc ) => dc.id === rememberedDcId )
		setSelectedDcIdState( remembered?.id ?? dcs[ 0 ].id )
	}, [ loading, dcs, rememberedDcId ] )

	// Remembering happens in the handler that owns the change rather than in an
	// effect watching the value: this is the only way the choice moves, so the
	// write belongs beside the decision.
	const setSelectedDcId = useCallback( ( id: string | null ) => {
		setSelectedDcIdState( id )
		rememberDcId( id )
	}, [ rememberDcId ] )

	const value = useMemo(
		() => ( { selectedDcId, setSelectedDcId, dcs, loading } ),
		[ selectedDcId, setSelectedDcId, dcs, loading ],
	)

	return (
		<DcAdminContext.Provider value={value}>
			{children}
		</DcAdminContext.Provider>
	)
}

export function useDcAdminContext() {
	const ctx = useContext( DcAdminContext )
	if ( !ctx ) {
		throw new Error( 'useDcAdminContext must be used within DcAdminProvider' )
	}
	return ctx
}
